import { useMemo, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { Label } from "@atlaskit/form";
import LinkIcon from "@atlaskit/icon/core/link";
import Popup from "@atlaskit/popup";
import type { TriggerProps } from "@atlaskit/popup/types";
import { Flex, xcss } from "@atlaskit/primitives";
import Textfield from "@atlaskit/textfield";
import { token } from "@atlaskit/tokens";

import { ANALYTICS_PREFIX } from "../../../../common/constants";
import { generateShareLink, POPOVER_DURATION } from "../utils";

import messages from "./messages";
import { ShareLinkCopyButtonPopup } from "./share-link-copy-button-popup";

export const ShareLink = () => {
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [showCopiedPopup, setShowCopiedPopup] = useState(false);
    const [popupTimeout, setPopupTimeout] = useState<NodeJS.Timeout | null>(
        null
    );

    const { link: shareLink } = useMemo(() => generateShareLink(), []);

    const triggerPopup = () => {
        setShowCopiedPopup(true);
        if (popupTimeout) {
            clearTimeout(popupTimeout);
        }

        const hidePopupTimeout = setTimeout(() => {
            setShowCopiedPopup(false);
        }, POPOVER_DURATION);
        setPopupTimeout(hidePopupTimeout);
    };

    const handleClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: `${ANALYTICS_PREFIX}InviteCopyLinkButton`,
            eventType: "ui"
        }).fire();

        navigator.clipboard
            .writeText(shareLink)
            .catch((error) => {
                createAnalyticsEvent({
                    action: "failed",
                    actionSubject: "copyLinkToClipboard",
                    eventComponent: `${ANALYTICS_PREFIX}InviteCopyLinkButton`,
                    eventType: "track",
                    attributes: {
                        error: error.message
                    }
                }).fire();
            })
            .finally(() => triggerPopup());
    };

    const handleOnClose = () => {
        if (popupTimeout) {
            clearTimeout(popupTimeout);
        }
        setShowCopiedPopup(false);
    };

    const renderTrigger = (triggerProps: TriggerProps) => (
        <Button
            {...triggerProps}
            css={copyLinkButtonStyles}
            iconBefore={<LinkIcon label="Copy share link" />}
            onClick={handleClick}
        >
            {formatMessage(messages.copyShareLinkCta)}
        </Button>
    );

    return (
        <Flex direction="column" xcss={fullWidth}>
            <div css={copyLinkTextBoxLabelStyles}>
                <Label htmlFor="copy-share-link">
                    {formatMessage(messages.linkShare)}
                </Label>
            </div>

            <Flex
                gap="space.100"
                justifyContent="space-between"
                alignItems="center"
            >
                <Textfield
                    name="copy-share-link"
                    id="basic-textfield"
                    value={shareLink}
                    isReadOnly
                    css={copyLinkTextBoxStyles}
                />

                <Popup
                    shouldRenderToParent
                    isOpen={showCopiedPopup}
                    onClose={handleOnClose}
                    placement="top-end"
                    content={ShareLinkCopyButtonPopup}
                    trigger={renderTrigger}
                />
            </Flex>
        </Flex>
    );
};

const fullWidth = xcss({
    width: "100%"
});

const copyLinkButtonStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "1px solid #dcdee1 !important",
    borderRadius: "4px !important",
    backgroundColor: `${token("color.background.input")} !important`,
    height: "44px !important",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& > span"]: {
        marginBlock: "auto",
        fontSize: "14px !important",
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#505258 !important"
    }
});

const copyLinkTextBoxLabelStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& > label"]: {
        font: token("font.body.small"),
        fontWeight: token("font.weight.bold"),
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#505258"
    }
});

const copyLinkTextBoxStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > [data-ds--text-field--input]": {
        minHeight: "44px",
        backgroundColor: token("color.background.disabled"),
        border: "1px solid",
        borderColor: token("color.border"),
        borderRadius: token("radius.small", "3px")
    },
    ["&[data-ds--text-field--container]"]: {
        border: "none",
        flexGrow: 1
    }
});
